// Trending digest: compute "weekly movers" from watchlist + rank snapshots.
//
// For each watched app with >= 2 snapshots, compare the most recent snapshot
// to the reference snapshot taken ~windowDays ago (or the oldest available).
// Apps with measurable net delta in mean popularity score are returned as
// AppMover records, ranked by absolute delta descending.
//
// All functions accept an optional `store` parameter for unit testing.

import { readStore, isoDateLabel } from './rankSnapshots.js';

/**
 * @typedef {object} AppMover
 * @property {string|number} trackId
 * @property {string} trackName
 * @property {string} artworkUrl
 * @property {number} netDelta      - Mean popularity-score change (positive = up)
 * @property {number} countriesUp   - Number of countries where score improved
 * @property {number} countriesDown - Number of countries where score declined
 * @property {string} snapshotFrom  - ISO timestamp of the reference (older) snapshot
 * @property {string} snapshotTo    - ISO timestamp of the newer snapshot
 */

/**
 * Find the index of the snapshot in `snapshots` whose timestamp is closest
 * to (and no later than) `targetIso`. Returns 0 when all snapshots are newer.
 *
 * @param {import('./rankSnapshots.js').AppSnapshot[]} snapshots
 * @param {string} targetIso
 * @returns {number}
 */
export function findClosestSnapshotIndex(snapshots, targetIso) {
  let best = 0;
  for (let i = 0; i < snapshots.length; i++) {
    if (snapshots[i].snappedAt <= targetIso) {
      best = i;
    }
  }
  return best;
}

/**
 * Compute weekly movers from the user's watchlist and stored rank snapshots.
 *
 * @param {Array<{trackId: string|number, trackName: string, artworkUrl: string}>} watchlist
 * @param {string} nowIso        - Current time as ISO-8601 (injected for testability)
 * @param {number} [windowDays=7]  - Look-back window for reference snapshot
 * @param {number} [minDelta=0.5]  - Minimum |netDelta| to include an app
 * @param {Storage} [store]        - Optional custom store for unit tests
 * @returns {AppMover[]}
 */
export function computeMovers(watchlist, nowIso, windowDays = 7, minDelta = 0.5, store) {
  if (!Array.isArray(watchlist) || watchlist.length === 0) return [];

  const snapshotData = readStore(store);
  const windowMs = windowDays * 24 * 60 * 60 * 1000;
  const cutoffIso = new Date(new Date(nowIso).getTime() - windowMs).toISOString();

  const movers = [];

  for (const app of watchlist) {
    const key = String(app.trackId);
    const entry = snapshotData[key];
    if (!entry || entry.snapshots.length < 2) continue;

    const snapshots = entry.snapshots;
    const newest = snapshots[snapshots.length - 1];

    // Reference: snapshot <= cutoff, searched in all snapshots except newest
    const candidates = snapshots.slice(0, -1);
    const refIdx = findClosestSnapshotIndex(candidates, cutoffIso);
    const reference = candidates[refIdx];

    if (reference.snappedAt === newest.snappedAt) continue;

    let sumDelta = 0;
    let count = 0;
    let up = 0;
    let down = 0;

    for (const [cc, newestCountry] of Object.entries(newest.countries)) {
      const refCountry = reference.countries[cc];
      if (!refCountry) continue;
      const ns = newestCountry.popularityScore;
      const rs = refCountry.popularityScore;
      if (ns === null || rs === null) continue;

      const delta = ns - rs;
      sumDelta += delta;
      count++;
      if (delta > 0) up++;
      else if (delta < 0) down++;
    }

    if (count === 0) continue;
    const netDelta = sumDelta / count;
    if (Math.abs(netDelta) < minDelta) continue;

    movers.push({
      trackId: app.trackId,
      trackName: app.trackName,
      artworkUrl: app.artworkUrl,
      netDelta,
      countriesUp: up,
      countriesDown: down,
      snapshotFrom: reference.snappedAt,
      snapshotTo: newest.snappedAt,
    });
  }

  movers.sort((a, b) => Math.abs(b.netDelta) - Math.abs(a.netDelta));
  return movers;
}

export { isoDateLabel };
